import type { PropertyPurposeType } from '@/types/api';
import type { State, UnaccreditedBrokerAggregatorEnum, ExitStrategy } from '@/api/enums';
import type { components } from '@/types/schemas/api.main';

interface PeriodDefinition {
  label: string;
  multiplier: number;
}

export const periodEnum = ['week', 'fortnight', 'month', 'quarter', 'year'] as const;

export const periodType: {
  [K in (typeof periodEnum)[number]]: PeriodDefinition;
} = {
  week: { label: 'weekly', multiplier: 52 },
  fortnight: { label: 'fortnightly', multiplier: 26 },
  month: { label: 'monthly', multiplier: 12 },
  quarter: { label: 'quarterly', multiplier: 4 },
  year: { label: 'annually', multiplier: 1 },
};

/** Allowed frequency options for company net profit (excludes weekly and fortnightly). Default is annual. */
export const netProfitPeriodOptions = ['year', 'quarter', 'month'] as const;
export const NET_PROFIT_DEFAULT_FREQUENCY = 'year' as const;

export const EquityReleasePurposeOptions = [
  { label: 'Contribution towards the purchase of a land lease, retirement or community property', eligible: true },
  { label: 'Deposit for the purchase of new property', eligible: true },
  { label: 'Contribution to the purchase of a new home', eligible: true },
  { label: 'Non structural renovations and home improvements', eligible: false },
  { label: 'Travel, entertainment or leisure', eligible: false },
  { label: 'Purchase of a car', eligible: false },
];

/** The backend `SelfEmployedDocuments` values. */
export type ProvidedDocument = NonNullable<components['schemas']['FundsSelfEmployed']['providedDocument']>;

export const ProvidedDocumentOptions = {
  SIMPLE_SELF_EMPLOYED: 'simple_self_employed',
  ONE_YEAR_FINANCIALS: 'one_year_financials',
  TAX_RETURN_TWO_YEARS: 'tax_return_two_years',
  ACCOUNTANTS_LETTER: 'accountants_letter',
  SIX_MONTHS_BAS: 'six_months_bas',
  SIX_MONTHS_BANK_STATEMENTS: 'six_months_bank_statements',
  OTHER: 'other',
} satisfies Record<string, ProvidedDocument>;

export const ProvidedDocumentOptionsLabels: Record<ProvidedDocument, string> = {
  [ProvidedDocumentOptions.SIMPLE_SELF_EMPLOYED]: 'Simple self employed',
  [ProvidedDocumentOptions.ONE_YEAR_FINANCIALS]: 'One year financials',
  [ProvidedDocumentOptions.TAX_RETURN_TWO_YEARS]: 'Two years most recent tax returns',
  [ProvidedDocumentOptions.ACCOUNTANTS_LETTER]: "Bridgit/CHL/Finsure Accountant's Letter",
  [ProvidedDocumentOptions.SIX_MONTHS_BAS]: 'Most recent 6 months BAS',
  [ProvidedDocumentOptions.SIX_MONTHS_BANK_STATEMENTS]: 'Most recent 6 months business bank statements',
  [ProvidedDocumentOptions.OTHER]: 'Other',
};

// JS-10203: the provided documents that only exist once
// `js-10203-simple-and-1-year-financials-self-employed-income` is on.
const JS_10203_PROVIDED_DOCUMENTS: readonly ProvidedDocument[] = [
  ProvidedDocumentOptions.SIMPLE_SELF_EMPLOYED,
  ProvidedDocumentOptions.ONE_YEAR_FINANCIALS,
];

/**
 * The `BxSelect` list items (value → label, in display order) for a self-employed income's
 * provided document. With the JS-10203 flag off the two new options are hidden, except one the
 * income already holds — an application saved while the flag was on keeps displaying its value.
 */
export const providedDocumentListItems = (isFeatureEnabled: boolean, selectedValue?: string): Record<string, string> =>
  Object.fromEntries(
    Object.values(ProvidedDocumentOptions)
      .filter((value) => isFeatureEnabled || !JS_10203_PROVIDED_DOCUMENTS.includes(value) || value === selectedValue)
      .map((value) => [value, ProvidedDocumentOptionsLabels[value]]),
  );

export const AdditionalFundsPurpose = {
  FINANCIAL_CONSOLIDATION: 'financial_consolidation',
  COSMETIC_IMPROVEMENTS_BEFORE_SELLING: 'cosmetic_improvements_before_selling',
  PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME: 'purchase_retirement_land_lease_or_community_home',
  PURCHASE_VACANT_LAND: 'purchase_vacant_land',
  BUSINESS_PURPOSE: 'business_purpose',
  COMPLETE_PROJECT_FINANCE: 'complete_project_finance',
  /** Frontend-only sentinel; no additional_funds_purposes row */
  PURCHASE_ONLY: 'purchase_only',
  OTHER: 'other',
} as const;

export const AdditionalFundsPurposeOptions = {
  [AdditionalFundsPurpose.FINANCIAL_CONSOLIDATION]: 'Financial consolidation',
  [AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING]: 'Cosmetic improvements before selling',
  [AdditionalFundsPurpose.OTHER]: 'Other (select if there is more than 1 loan purpose)',
};

export const CommercialAdditionalFundsPurposeOptions = {
  [AdditionalFundsPurpose.BUSINESS_PURPOSE]: 'Business purpose',
  [AdditionalFundsPurpose.COMPLETE_PROJECT_FINANCE]: 'Complete project finance',
};

export const LoanPurposeAdditionalFundsPurposeOptions = {
  [AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING]: 'Cosmetic improvements before selling',
  [AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME]:
    'Purchase retirement, land lease or community home',
  [AdditionalFundsPurpose.PURCHASE_VACANT_LAND]: 'Purchase vacant land',
  [AdditionalFundsPurpose.OTHER]: 'Other (select if there is more than 1 loan purpose)',
};

export const BridgingPurposeOptions = [
  { label: 'Home renovation', eligible: true },
  { label: 'Other', eligible: true },
];

/**
 * ERHL minimum loan amount (JS-10144, effective 28 Sep 2026). The application form uses it as the
 * initial lump sum default, slider floor and soft-warning threshold; it is UI guidance only — the
 * validation schema deliberately enforces no minimum so the application can still be progressed.
 */
export const ERHL_MIN_LOAN_AMOUNT = 300_000;

export const ErhlLoanPurpose = {
  COSMETIC_HOME_IMPROVEMENTS: 'cosmetic_home_improvements',
  MEDICAL_AGED_CARE: 'medical_aged_care',
  CONSOLIDATE_DEBTS: 'consolidate_debts',
  INCREASE_PERSONAL_CASHFLOW: 'increase_personal_cashflow',
  LIFESTYLE_SUPPORT: 'lifestyle_support',
  INVESTMENT_PURPOSES: 'investment_purposes',
  GIFTING_FAMILY_ESTATE_PLANNING: 'gifting_family_estate_planning',
  CHANGE_IN_OWNERSHIP: 'change_in_ownership',
  REFINANCE_EXISTING_PROPERTY: 'refinance_existing_property',
  PURCHASE_NEW_PROPERTY: 'purchase_new_property',
  PURCHASE_NON_SECURITY_PROPERTY: 'purchase_non_security_property',
  OTHER: 'other',
} as const;

export type ErhlLoanPurposeValue = (typeof ErhlLoanPurpose)[keyof typeof ErhlLoanPurpose];

export const ErhlLoanPurposeOptions: Record<ErhlLoanPurposeValue, string> = {
  [ErhlLoanPurpose.COSMETIC_HOME_IMPROVEMENTS]: 'Cosmetic home improvements',
  [ErhlLoanPurpose.INCREASE_PERSONAL_CASHFLOW]: 'Increase personal cashflow',
  [ErhlLoanPurpose.GIFTING_FAMILY_ESTATE_PLANNING]: 'Family gifting or wealth transfer',
  [ErhlLoanPurpose.PURCHASE_NEW_PROPERTY]: 'Purchase a new property',
  [ErhlLoanPurpose.PURCHASE_NON_SECURITY_PROPERTY]: 'Purchase of non-security property',
  [ErhlLoanPurpose.CHANGE_IN_OWNERSHIP]: 'Change in ownership',
  [ErhlLoanPurpose.CONSOLIDATE_DEBTS]: 'Financial consolidation',
  [ErhlLoanPurpose.MEDICAL_AGED_CARE]: 'Medical or aged care costs',
  [ErhlLoanPurpose.INVESTMENT_PURPOSES]: 'Investment purposes',
  [ErhlLoanPurpose.REFINANCE_EXISTING_PROPERTY]: 'Refinance existing property',
  [ErhlLoanPurpose.OTHER]: 'Other',
  [ErhlLoanPurpose.LIFESTYLE_SUPPORT]: 'Lifestyle support',
};

export const CustomerErhlLoanPurposeOptions: Record<ErhlLoanPurposeValue, string> = {
  ...ErhlLoanPurposeOptions,
  [ErhlLoanPurpose.INCREASE_PERSONAL_CASHFLOW]: 'Increase in personal cashflow',
  [ErhlLoanPurpose.PURCHASE_NEW_PROPERTY]: 'Purchase of a new property',
  [ErhlLoanPurpose.REFINANCE_EXISTING_PROPERTY]: 'Refinance of existing property',
};

export const ErhlLoanPurposeTooltips: Partial<Record<ErhlLoanPurposeValue, string>> = {
  [ErhlLoanPurpose.COSMETIC_HOME_IMPROVEMENTS]:
    'Funds used for non-structural renovations, repairs or modifications to the home, including accessibility upgrades and cosmetic works.',
  [ErhlLoanPurpose.MEDICAL_AGED_CARE]: 'Medical expenses, ongoing care fees or other health-related costs.',
  [ErhlLoanPurpose.CONSOLIDATE_DEBTS]:
    'Funds used to pay out existing debts, such as personal loans, credit cards or other liabilities.',
  [ErhlLoanPurpose.INCREASE_PERSONAL_CASHFLOW]:
    "General living expenses, lifestyle costs, or other day-to-day needs that support the applicant's quality of life.",
  [ErhlLoanPurpose.LIFESTYLE_SUPPORT]:
    "General living expenses, lifestyle costs, or other day-to-day needs that support the client's quality of life.",
  [ErhlLoanPurpose.INVESTMENT_PURPOSES]:
    'Funds directed toward financial investments such as shares, managed funds or other assets.',
  [ErhlLoanPurpose.GIFTING_FAMILY_ESTATE_PLANNING]:
    'Funds provided to family members, such as an early inheritance, general family support, or contributing toward a property purchase.',
  [ErhlLoanPurpose.CHANGE_IN_OWNERSHIP]:
    "Change to the property's ownership structure, such as a buyout of a co-owner's share.",
};

export const CustomerErhlLoanPurposeTooltips: Partial<Record<ErhlLoanPurposeValue, string>> = {
  [ErhlLoanPurpose.COSMETIC_HOME_IMPROVEMENTS]:
    'Funds used for non-structural renovations, repairs or modifications to the home, including accessibility upgrades and cosmetic works.',
  [ErhlLoanPurpose.MEDICAL_AGED_CARE]: 'Medical expenses, ongoing care fees or other health-related costs.',
  [ErhlLoanPurpose.CONSOLIDATE_DEBTS]:
    'Funds used to pay out existing debts, such as personal loans, credit cards or other liabilities.',
  [ErhlLoanPurpose.INCREASE_PERSONAL_CASHFLOW]:
    'General living expenses, lifestyle costs, or other day-to-day needs that support your quality of life.',
  [ErhlLoanPurpose.LIFESTYLE_SUPPORT]:
    'General living expenses, lifestyle costs, or other day-to-day needs that support your quality of life.',
  [ErhlLoanPurpose.INVESTMENT_PURPOSES]:
    'Funds directed toward financial investments such as shares, managed funds or other assets.',
  [ErhlLoanPurpose.GIFTING_FAMILY_ESTATE_PLANNING]:
    'Funds provided to family members, such as an early inheritance, general family support, or contributing toward a property purchase.',
  [ErhlLoanPurpose.CHANGE_IN_OWNERSHIP]:
    "Change to the property's ownership structure, such as a buyout of a co-owner's share.",
};

export const ErhlEquityAccessMethod = {
  LUMP_SUM_ONLY: 'lump_sum_only',
  LUMP_SUM_AND_MONTHLY_DRAWDOWN: 'lump_sum_and_monthly_drawdown',
} as const;

export const ErhlEquityAccessMethodTooltips: Record<ErhlEquityAccessMethodValue, string> = {
  [ErhlEquityAccessMethod.LUMP_SUM_ONLY]: 'The full loan amount is paid at settlement',
  [ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN]:
    'In addition to the lump sum at settlement, the applicant receives ongoing scheduled payments.',
};
export const CustomerErhlEquityAccessMethodTooltips: Record<ErhlEquityAccessMethodValue, string> = {
  [ErhlEquityAccessMethod.LUMP_SUM_ONLY]: 'The full loan amount is paid at settlement',
  [ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN]:
    'In addition to the lump sum at settlement, you will receive ongoing scheduled payments.',
};

export type ErhlEquityAccessMethodValue = (typeof ErhlEquityAccessMethod)[keyof typeof ErhlEquityAccessMethod];

export const ErhlEquityAccessMethodOptions: Record<ErhlEquityAccessMethodValue, string> = {
  [ErhlEquityAccessMethod.LUMP_SUM_ONLY]: 'Lump sum only',
  [ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN]: 'Lump sum and scheduled equity release',
};
export const ErhlPropertyType = {
  RESIDENTIAL: 'residential',
  RURAL_RESIDENTIAL: 'rural_residential',
  OFF_PLAN_RESIDENTIAL: 'off_plan_residential',
  COMMERCIAL: 'commercial',
  RETIREMENT_LAND_LEASE_COMMUNITY_HOME: 'retirement_land_lease_or_community_home',
  VACANT_LAND: 'vacant_land',
} as const;

export const erhlPropertyTypeOptions = {
  [ErhlPropertyType.RESIDENTIAL]: 'Residential',
  [ErhlPropertyType.RURAL_RESIDENTIAL]: 'Rural residential',
  [ErhlPropertyType.OFF_PLAN_RESIDENTIAL]: 'Off the plan residential',
  [ErhlPropertyType.COMMERCIAL]: 'Commercial',
  [ErhlPropertyType.RETIREMENT_LAND_LEASE_COMMUNITY_HOME]: 'Retirement, land-lease or community home',
  [ErhlPropertyType.VACANT_LAND]: 'Vacant land',
};

export type ErhlPropertyTypeValue = (typeof ErhlPropertyType)[keyof typeof ErhlPropertyType];

/** ERHL property types for supporting properties — excludes retirement/land-lease (purchase-only) */
export const erhlSupportingPropertyTypeOptions: Partial<Record<ErhlPropertyTypeValue, string>> = {
  [ErhlPropertyType.RESIDENTIAL]: 'Residential',
  [ErhlPropertyType.RURAL_RESIDENTIAL]: 'Rural residential',
  [ErhlPropertyType.OFF_PLAN_RESIDENTIAL]: 'Off the plan residential',
  [ErhlPropertyType.COMMERCIAL]: 'Commercial',
  [ErhlPropertyType.VACANT_LAND]: 'Vacant land',
};

/** ERHL declaration question discriminators (matches API enum) */
export const ErhlDeclarationQuestionType = {
  INCOME_CHANGES_BEFORE_RETIREMENT: 'income_changes_before_retirement',
  RETIREMENT_INCOME_IMPACT: 'retirement_income_impact',
  EXPECTED_RETIREMENT_INCOME: 'expected_retirement_income',
  FINANCIAL_CHANGES_DURING_LOAN: 'financial_changes_during_loan',
  HAS_FINANCIAL_OBLIGATIONS: 'has_financial_obligations',
  LIVING_EXPENSES_CHANGE_AFTER_RETIREMENT: 'living_expenses_change_after_retirement',
  EXPENSE_CHANGE_AFTER_RETIREMENT: 'expense_change_after_retirement',
} as const;

export const ExitStrategyValue = {
  SECURITY_REFINANCE: 'security_refinance',
  SALE_OF_NON_SECURITY_ASSETS: 'sale_of_non_security_assets',
  OTHER: 'other',
} as const;

export const ExitStrategyOptions: Partial<Record<ExitStrategy, string>> = {
  security_refinance: 'Refinancing within the loan term',
  sale_of_non_security_assets: 'Selling assets to cover any remaining balance',
  other: 'Other',
};

/** ERHL-specific exit strategy options matching the API enum */
export const ErhlExitStrategyValue = {
  DOWNSIZING_PRIMARY_RESIDENCE: 'downsizing_primary_residence',
  SALE_OF_INVESTMENT_PROPERTY: 'sale_of_investment_property',
  SALE_OF_SHARES_OR_OTHER_ASSETS: 'sale_of_shares_or_other_assets',
  SUPERANNUATION_LUMP_SUM_WITHDRAWAL: 'superannuation_lump_sum_withdrawal',
  INHERITANCE: 'inheritance',
  REFINANCE_TO_REVERSE_MORTGAGE: 'refinance_to_reverse_mortgage',
  OTHER: 'other',
} as const;

export type ErhlExitStrategyEnum = (typeof ErhlExitStrategyValue)[keyof typeof ErhlExitStrategyValue];

export const ErhlExitStrategyOptions: Record<ErhlExitStrategyEnum, string> = {
  downsizing_primary_residence: 'Downsizing primary residence',
  sale_of_investment_property: 'Sale of investment property',
  sale_of_shares_or_other_assets: 'Sale of shares or other assets',
  superannuation_lump_sum_withdrawal: 'Superannuation lump sum withdrawal',
  inheritance: 'Inheritance',
  refinance_to_reverse_mortgage: 'Refinance to reverse mortgage',
  other: 'Other',
};

export const stateList = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

export const LoanType = {
  BRIDGING: 'buy_now',
  EQUITY_RELEASE: 'equity_release',
  SINGLE_SECURITY: 'single_security',
  EQUITY_RELEASE_HOME_LOAN: 'equity_release_home_loan',
  PURCHASE_ONLY: 'purchase_only',
} as const;

export const LoanProductType = {
  COMMERCIAL: 'commercial',
  RESIDENTIAL: 'residential',
} as const;

export type LoanProductTypeEnum = (typeof LoanProductType)[keyof typeof LoanProductType];

export const AssessmentType = {
  REGULATED: 'regulated',
  UNREGULATED: 'unregulated',
} as const;

export type AssessmentTypeEnum = (typeof AssessmentType)[keyof typeof AssessmentType];

export const FinishType = {
  BRIDGE_LOAN: 'bridge_loan',
  SINGLE_SECURITY: 'single_security',
} as const;

// -------------------------------------------------------------------------------------------
// TODO: this entire block of code should draw property type values from `src/api/enums/PropertyType.enum.ts`
// so that the backend and swagger definitions are the single / only source of truth for what this data is
export const PropertyType = {
  RESIDENTIAL: 'residential',
  RURAL_RESIDENTIAL: 'rural_residential',
  OFF_PLAN_RESIDENTIAL: 'off_plan_residential',
  COMMERCIAL: 'commercial',
  RETIREMENT_LAND_OR_COMMUNITY_HOME: 'retirement_land_lease_or_community_home',
  VACANT_LAND: 'vacant_land',
} as const;

export const standardBridgeTypes = [
  PropertyType.RESIDENTIAL,
  PropertyType.RURAL_RESIDENTIAL,
  PropertyType.OFF_PLAN_RESIDENTIAL,
];

export const PropertyToFundsPurposeMap = {
  [PropertyType.RESIDENTIAL]: null,
  [PropertyType.RURAL_RESIDENTIAL]: null,
  [PropertyType.OFF_PLAN_RESIDENTIAL]: null,
  [PropertyType.COMMERCIAL]: null,
  [PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME]:
    AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME,
  [PropertyType.VACANT_LAND]: AdditionalFundsPurpose.PURCHASE_VACANT_LAND,
};

export const propertyTypeOptions = {
  [PropertyType.RESIDENTIAL]: 'Residential',
  [PropertyType.RURAL_RESIDENTIAL]: 'Rural Residential',
  [PropertyType.OFF_PLAN_RESIDENTIAL]: 'Off-the-plan Residential',
  [PropertyType.COMMERCIAL]: 'Commercial',
  [PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME]: 'Retirement, land-lease or community home',
  [PropertyType.VACANT_LAND]: 'Vacant Land',
};

export const incomingPropertyTypeOptions = {
  [PropertyType.RESIDENTIAL]: 'Residential',
  [PropertyType.RURAL_RESIDENTIAL]: 'Rural Residential',
  [PropertyType.OFF_PLAN_RESIDENTIAL]: 'Off-the-plan Residential',
  [PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME]: 'Retirement, land-lease or community home',
  [PropertyType.VACANT_LAND]: 'Vacant Land',
};

export const BrokerPropertyType = {
  RESIDENTIAL: 'residential',
  RURAL_RESIDENTIAL: 'rural_residential',
  OFF_PLAN_RESIDENTIAL: 'off_plan_residential',
  COMMERCIAL: 'commercial',
  RETIREMENT_LAND_LEASE_COMMUNITY_HOME: 'retirement_land_lease_or_community_home',
  VACANT_LAND: 'vacant_land',
} as const;

export const outgoingPropertyTypeOptions = {
  [PropertyType.RESIDENTIAL]: 'Residential',
  [PropertyType.RURAL_RESIDENTIAL]: 'Rural Residential',
  [PropertyType.OFF_PLAN_RESIDENTIAL]: 'Off-the-plan Residential',
  [PropertyType.COMMERCIAL]: 'Commercial',
  [PropertyType.VACANT_LAND]: 'Vacant Land',
};

// D2C flow options (no Vacant Land)
export const d2cOutgoingPropertyTypeOptions = {
  [PropertyType.RESIDENTIAL]: 'Residential',
  [PropertyType.RURAL_RESIDENTIAL]: 'Rural Residential',
  [PropertyType.OFF_PLAN_RESIDENTIAL]: 'Off-the-plan Residential',
  [PropertyType.COMMERCIAL]: 'Commercial',
};

// Commercial broker flow options (no Vacant Land)
export const commercialIncomingPropertyTypeOptions = {
  [BrokerPropertyType.RESIDENTIAL]: 'Residential',
  [BrokerPropertyType.RURAL_RESIDENTIAL]: 'Rural Residential',
  [BrokerPropertyType.OFF_PLAN_RESIDENTIAL]: 'Off the plan Residential',
  [BrokerPropertyType.COMMERCIAL]: 'Commercial',
  [BrokerPropertyType.RETIREMENT_LAND_LEASE_COMMUNITY_HOME]: 'Retirement, land-lease or community home',
};

export const commercialOutgoingPropertyTypeOptions = {
  [PropertyType.RESIDENTIAL]: 'Residential',
  [PropertyType.RURAL_RESIDENTIAL]: 'Rural Residential',
  [PropertyType.OFF_PLAN_RESIDENTIAL]: 'Off-the-plan Residential',
  [PropertyType.COMMERCIAL]: 'Commercial',
};

export const propertyTypesWithPurpose = [
  PropertyType.RESIDENTIAL,
  PropertyType.RURAL_RESIDENTIAL,
  PropertyType.OFF_PLAN_RESIDENTIAL,
  PropertyType.VACANT_LAND,
];
// -------------------------------------------------------------------------------------------

export const RelationshipStatus = {
  SINGLE: 'single',
  MARRIED_OR_DEFACTO: 'married_or_defacto',
} as const;

export const relationshipStatusOptions = {
  [RelationshipStatus.SINGLE]: 'Single',
  [RelationshipStatus.MARRIED_OR_DEFACTO]: 'Married/Defacto',
};

export const RelationshipType = {
  MARRIED_OR_DEFACTO: 'married_or_defacto',
  FRIEND: 'friend',
  SIBLING: 'sibling',
  OTHER: 'other',
} as const;

export const relationshipTypeOptions = {
  [RelationshipType.MARRIED_OR_DEFACTO]: 'Married/Defacto',
  [RelationshipType.FRIEND]: 'Friend',
  [RelationshipType.SIBLING]: 'Sibling',
  [RelationshipType.OTHER]: 'Other',
};

export const NextOfKinRelationship = {
  SPOUSE_PARTNER: 'spouse_partner',
  CHILD: 'child',
  SIBLING: 'sibling',
  PARENT: 'parent',
  LEGAL_REPRESENTATIVE: 'legal_representative',
  OTHER: 'other',
} as const;

export const nextOfKinRelationshipOptions: Record<string, string> = {
  [NextOfKinRelationship.SPOUSE_PARTNER]: 'Spouse/Partner',
  [NextOfKinRelationship.CHILD]: 'Child',
  [NextOfKinRelationship.SIBLING]: 'Sibling',
  [NextOfKinRelationship.PARENT]: 'Parent',
  [NextOfKinRelationship.LEGAL_REPRESENTATIVE]: 'Legal representative',
  [NextOfKinRelationship.OTHER]: 'Other',
};

export const Gender = {
  MALE: 'male',
  FEMALE: 'female',
  PREFER_NOT_TO_SAY: 'prefer_not_to_say',
  OTHER: 'other',
} as const;

export const genderOptions = {
  [Gender.MALE]: 'Male',
  [Gender.FEMALE]: 'Female',
  [Gender.PREFER_NOT_TO_SAY]: 'Prefer not to say',
  [Gender.OTHER]: 'Prefer to self describe as (please specify)',
};

export const ApplicantType = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const;

export const EmploymentType = {
  FULL_TIME: 'full_time',
  PART_TIME: 'part_time',
  CASUAL: 'casual',
  SELF_EMPLOYED: 'self_employed',
  UNEMPLOYED: 'unemployed',
  RETIRED: 'retired',
} as const;

export const employmentTypeOptions = {
  [EmploymentType.FULL_TIME]: { label: 'Full-time employment', description: 'Full-time', employed: true },
  [EmploymentType.PART_TIME]: { label: 'Part-time employment', description: 'Part-time', employed: true },
  [EmploymentType.CASUAL]: { label: 'Casual/temporary employment', description: 'Casual/temporary', employed: true },
  [EmploymentType.SELF_EMPLOYED]: {
    label: 'Self-employed/contractor',
    description: 'Self-employed/contractor',
    employed: true,
  },
  [EmploymentType.RETIRED]: { label: "No income. I'm retired", description: 'Retired', employed: false },
  [EmploymentType.UNEMPLOYED]: { label: "No income. I'm unemployed", description: 'Unemployed', employed: false },
};

export const brokerEmploymentTypeOptions = {
  ...employmentTypeOptions,
  [EmploymentType.RETIRED]: { ...employmentTypeOptions[EmploymentType.RETIRED], label: 'Retired' },
  [EmploymentType.UNEMPLOYED]: { ...employmentTypeOptions[EmploymentType.UNEMPLOYED], label: 'No income/unemployed' },
};

export const DepositType = {
  CASH: 'cash_payment',
  BOND: 'deposit_bond',
} as const;

export const depositTypeOptions = {
  [DepositType.CASH]: 'Cash payment',
  [DepositType.BOND]: 'Deposit bond',
};

export const StampDutyStatus = {
  AT_SETTLEMENT: 'at_settlement',
  PRIOR_TO_SETTLEMENT: 'prior_to_settlement',
  ALREADY_PAID: 'already_paid',
} as const;

export const PropertyPurpose = {
  OWNER_OCCUPIED: 'owner_occupied',
  INVESTMENT_PROPERTY: 'investment_property',
} as const;

export const propertyPurposeOptions: Record<PropertyPurposeType, string> = {
  [PropertyPurpose.OWNER_OCCUPIED]: 'A home to live in',
  [PropertyPurpose.INVESTMENT_PROPERTY]: 'An investment property',
};

export const stampDutyStatusOptions = {
  [StampDutyStatus.AT_SETTLEMENT]: 'To be paid on settlement',
  [StampDutyStatus.PRIOR_TO_SETTLEMENT]: 'To be paid prior to settlement',
  [StampDutyStatus.ALREADY_PAID]: 'Already paid',
};

export const SaleStatus = {
  SOLD: 'sold',
  LISTED_FOR_SALE: 'listed_for_sale',
  NONE: 'no',
} as const;

export const saleStatusOptions = {
  [SaleStatus.SOLD]: 'Sold',
  [SaleStatus.LISTED_FOR_SALE]: 'Listed for sale',
  [SaleStatus.NONE]: 'Not yet listed',
};

export const enum LoanPeriod {
  LESS_THAN_MONTH = '1',
  IN_1_2_MONTHS = '2',
  IN_2_3_MONTHS = '3',
  IN_3_PLUS_MONTHS = '6',
  EXACT_DATE = 'Custom',
}

export const loanPeriodOptions = {
  [LoanPeriod.LESS_THAN_MONTH]: 'In less than a month',
  [LoanPeriod.IN_1_2_MONTHS]: 'In 1-2 months',
  [LoanPeriod.IN_2_3_MONTHS]: 'In 2-3 months',
  [LoanPeriod.IN_3_PLUS_MONTHS]: 'In 3+ months',
  [LoanPeriod.EXACT_DATE]: 'I know the exact date',
};

export const AssetType = {
  PROPERTY: 'property',
  SAVINGS: 'savings',
  SHARES: 'shares',
  SUPERANNUATION: 'superannuation',
  GIFTED_FUND: 'gifted_funds',
  MACHINERY: 'machinery',
  MOTOR_VEHICLES: 'motor_vehicles',
  EQUIPMENT: 'equipment',
} as const;

export const LiabilityType = {
  CARD: 'card',
  VEHICLE: 'vehicle',
  OTHER: 'other',
} as const;

export const CurrentKey = 'currentKeyV5';
/**
 * Resume pointer for the customer (D2C) ERHL journey. Kept separate from `CurrentKey`, which the
 * customer bridging and broker ERHL journeys restore from on load, so a D2C draft is never
 * rehydrated into either of their machines (or vice versa).
 */
export const ErhlCustomerCurrentKey = 'erhlCustomerCurrentKeyV1';
export const SAVINGS_CONTRIBUTION_TO_PURCHASE = 'Savings to contribute to purchase';
export const GIFTED_FUNDS_CONTRIBUTION_TO_PURCHASE = 'Gifted funds to contribute to purchase';

export const BROKER_TOKEN = 'broker-token';
export const BROKER_ID = 'broker-id';
export const CRM = 'crm';

export const enum DeclarationType {
  AGREEMENT = 'agreement',
}

export const enum DeclarationDescription {
  ACCEPTED = 'accepted',
}

export const expenseTypeDescriptions: Record<string, Record<string, string>> = {
  clothing: {
    shortFormTitle: 'Clothing and personal care',
    tooltip: 'Clothing, footwear, personal care products, cosmetics, hair services and accessories.',
  },
  groceries: {
    shortFormTitle: 'Groceries',
    tooltip: 'Food and non-alcoholic beverages, toiletries and cleaning products.',
  },
  government_and_education: {
    shortFormTitle: 'Government and general education',
    tooltip: 'Public or government, primary and secondary school tuition, school and sports fees.',
  },
  childcare: {
    shortFormTitle: 'Child care costs',
    tooltip: 'Child care costs.',
  },
  general_insurance: {
    shortFormTitle: 'General insurances',
    tooltip: 'Home, contents and vehicle insurances.',
  },
  personal_insurance: {
    shortFormTitle: 'Other personal insurances',
    tooltip: 'Life, personal and other insurances.',
  },
  private_health_insurance: {
    shortFormTitle: 'Private health insurance',
    tooltip: 'Private health insurance.',
  },
  communication: {
    shortFormTitle: 'Communication costs',
    tooltip: 'Phone, mobile phone, internet bills and other subscriptions.',
  },
  house_expenses: {
    shortFormTitle: 'Household expenses',
    tooltip: 'Rates, gas and electricity bills.',
  },
  transport: {
    shortFormTitle: 'Transportation',
    tooltip: 'Public transport, taxis and vehicle expenses.',
  },
  rent: {
    shortFormTitle: 'Rent',
    tooltip: 'Rent on primary residence (if applicable).',
  },
  other_property_fees: {
    shortFormTitle: 'Other property costs',
    tooltip: 'Owner-occupied and investment property body corporate fees.',
  },
  recreation: {
    shortFormTitle: 'Entertainment costs',
    tooltip: 'Recreation and entertainment, including takeaways and dining out.',
  },
  child_support: {
    shortFormTitle: 'Child support',
    tooltip: 'Child support and maintenance payments.',
  },
  others: {
    shortFormTitle: 'Other',
    tooltip: 'All other expenses not included above.',
  },
  education: {
    shortFormTitle: 'Education',
    tooltip: 'Education.',
  },
  insurance_premium: {
    shortFormTitle: 'Insurance',
    tooltip: 'Insurance.',
  },
  medical_health: {
    shortFormTitle: 'Medical',
    tooltip: 'Medical.',
  },
  home_property_cost: {
    shortFormTitle: 'Home property costs',
    tooltip: 'Home property costs.',
  },
  investment_property_cost: {
    shortFormTitle: 'Investment property costs',
    tooltip: 'Costs associated with any property owned for investment purposes.',
  },
  owner_occ_property_cost: {
    shortFormTitle: 'Owner occupier property costs',
    tooltip: 'Body corporate fees, strata fees and land tax on owner occupied principal place of residence.',
  },
  private_school_fees: {
    shortFormTitle: 'Private school fees',
    tooltip: 'Private school fees',
  },
} as const;

export const YesNoOptions = {
  yes: 'Yes',
  no: 'No',
} as const;

export const unaccreditedBrokerAggregators: Record<UnaccreditedBrokerAggregatorEnum, string> = {
  chl: 'Connective',
  finsure: 'Finsure',
  aussie: 'Aussie',
  outsource: 'Outsource Financial',
  mortgage_choice: 'Mortgage Choice',
  lmg: 'LMG',
  unknown: 'Other',
};

export const auStates: Partial<Record<State, string>> = {
  act: 'ACT',
  nsw: 'NSW',
  nt: 'NT',
  qld: 'QLD',
  sa: 'SA',
  tas: 'TAS',
  vic: 'VIC',
  wa: 'WA',
};

export const PropertyDetailOption = {
  PROPERTY_TO_PURCHASE: 'property_to_purchase',
  PROPERTY_TO_SELL: 'property_to_sell',
  SUPPORTING_PROPERTY: 'supporting_property',
} as const;

export type PropertyDetailOptionType = (typeof PropertyDetailOption)[keyof typeof PropertyDetailOption];

export const propertyDetailOptions: Record<PropertyDetailOptionType, string> = {
  [PropertyDetailOption.PROPERTY_TO_PURCHASE]: 'Property To Purchase',
  [PropertyDetailOption.PROPERTY_TO_SELL]: 'Property To Sell',
  [PropertyDetailOption.SUPPORTING_PROPERTY]: 'Supporting Property',
};

export const supportingPropertyTooltip =
  "A 'Supporting Property' is an existing property that isn't being bought or sold, but is used as additional security for Loan To Value Ratio purposes.";

export const commercialTypeOptions: Record<string, string> = {
  RESIDENTIAL_INVESTMENT: 'Residential Investment',
  RESIDENTIAL_RESIDUAL_STOCK: 'Residential Residual Stock',
  COMMERCIAL: 'Commercial',
  COMMERCIAL_RESIDUAL_STOCK: 'Commercial Residual Stock',
  OFFICE: 'Office',
  RETAIL: 'Retail',
  HOSPITALITY: 'Hospitality',
  INDUSTRIAL: 'Industrial/Warehouse',
  SPECIALISED_USE: 'Specialised Use',
  VACANT_LAND: 'Vacant Land / Land banks',
};

export type CommercialTypeEnum = keyof typeof commercialTypeOptions;

/** Granular quote commercial type options (House/Unit/Other) - used by PropertyDetailForm and when flag is false */
export const quoteCommercialTypeOptions: Record<string, string> = {
  RESIDENTIAL_INVESTMENT: 'Residential Investment',
  RESIDENTIAL_RESIDUAL_STOCK: 'Residential Residual Stock',
  COMMERCIAL_RESIDUAL_STOCK: 'Commercial Residual Stock',
  MIXED_USE: 'Mixed Use',
  OFFICE: 'Office',
  RETAIL: 'Retail',
  HOSPITALITY: 'Hospitality',
  INDUSTRIAL_WAREHOUSE: 'Industrial/Warehouse',
  SPECIALISED: 'Specialised - childcare, gaming zone, medical clinic, service station',
  VACANT_LAND_LAND_BANKS: 'Vacant Land / Land Banks',
};

export type QuoteCommercialTypeEnum = keyof typeof quoteCommercialTypeOptions;

export const residentialCommercialTypes = ['RESIDENTIAL_INVESTMENT', 'RESIDENTIAL_RESIDUAL_STOCK'] as const;
/** All commercial types that are residential (for hiding property purpose and defaulting to investment) */
export const quoteResidentialCommercialTypes = new Set([
  'RESIDENTIAL_INVESTMENT',
  'RESIDENTIAL_RESIDUAL_STOCK',
  'RESIDENTIAL_INVESTMENT_HOUSE',
  'RESIDENTIAL_INVESTMENT_UNIT',
  'RESIDENTIAL_INVESTMENT_OTHER',
  'RESIDENTIAL_RESIDUAL_STOCK_HOUSE',
  'RESIDENTIAL_RESIDUAL_STOCK_UNIT',
  'RESIDENTIAL_RESIDUAL_STOCK_OTHER',
]);

/** commercialType values that indicate a commercial property (product matrix flags as commercial) */
export const commercialPropertyTypes = new Set([
  'COMMERCIAL_RESIDUAL_STOCK',
  'MIXED_USE',
  'OFFICE',
  'RETAIL',
  'HOSPITALITY',
  'INDUSTRIAL_WAREHOUSE',
  'SPECIALISED',
  'SPECIALISED_USE',
  'VACANT_LAND_LAND_BANKS',
]);

export const ResidenceType = {
  HOUSE: 'house',
  UNIT: 'unit',
  OTHER: 'other',
} as const;

export type ResidenceTypeEnum = (typeof ResidenceType)[keyof typeof ResidenceType];

export const residenceTypeOptions: Record<ResidenceTypeEnum, string> = {
  [ResidenceType.HOUSE]: 'House',
  [ResidenceType.UNIT]: 'Unit',
  [ResidenceType.OTHER]: 'Other',
};

export const propertyUsedOptions: Record<PropertyPurposeType, string> = {
  [PropertyPurpose.OWNER_OCCUPIED]: 'Owner occupied',
  [PropertyPurpose.INVESTMENT_PROPERTY]: 'An investment property',
};

export const customerErhlPropertyUsedOptions: Record<PropertyPurposeType, string> = {
  ...propertyUsedOptions,
  [PropertyPurpose.INVESTMENT_PROPERTY]: 'Investment property',
};

export const BorrowerType = {
  INDIVIDUAL: 'individual',
  COMPANY: 'company',
  TRUST: 'trust',
} as const;

export type BorrowerTypeEnum = (typeof BorrowerType)[keyof typeof BorrowerType];

export const BorrowerTypeOptions: Record<BorrowerTypeEnum, string> = {
  [BorrowerType.INDIVIDUAL]: 'Individual',
  [BorrowerType.COMPANY]: 'Company',
  [BorrowerType.TRUST]: 'Trust',
};

/** Selection for "Are the borrowers individuals, or companies/trusts?" */
export const BorrowerTypeSelection = {
  INDIVIDUALS: 'individual',
  COMPANIES_TRUSTS: 'company_trust',
  BOTH: 'individual_and_company_trust',
} as const;

export type BorrowerTypeSelectionEnum = (typeof BorrowerTypeSelection)[keyof typeof BorrowerTypeSelection];

export const BorrowerTypeSelectionOptions: Record<BorrowerTypeSelectionEnum, string> = {
  [BorrowerTypeSelection.INDIVIDUALS]: 'Individual',
  [BorrowerTypeSelection.COMPANIES_TRUSTS]: 'Company/Trust',
  [BorrowerTypeSelection.BOTH]: 'Individuals & Company/Trust',
};

export const TrusteeType = {
  INDIVIDUAL: 'individual',
  COMPANY: 'company',
} as const;

export type TrusteeTypeEnum = (typeof TrusteeType)[keyof typeof TrusteeType];

export const TrusteeTypeOptions: Record<TrusteeTypeEnum, string> = {
  [TrusteeType.INDIVIDUAL]: 'Individual',
  [TrusteeType.COMPANY]: 'Company',
};

export const TrustStructure = {
  UNIT: 'unit',
  DISCRETIONARY: 'discretionary',
  HYBRID: 'hybrid',
} as const;

export type TrustStructureEnum = (typeof TrustStructure)[keyof typeof TrustStructure];

export const TrustStructureOptions: Record<TrustStructureEnum, string> = {
  [TrustStructure.UNIT]: 'Unit',
  [TrustStructure.DISCRETIONARY]: 'Discretionary',
  [TrustStructure.HYBRID]: 'Hybrid',
};

export const AppValues = {
  COMPANY_NAME: import.meta.env.VITE_APP_CUSTOM_THEME_AGGREGATOR ?? 'Bridgit',
  COMPANY_NAME_SHORT: import.meta.env.VITE_APP_CUSTOM_THEME_AGGREGATOR_SHORT_NAME ?? 'Bridgit',
  CONTACT: import.meta.env.VITE_APP_CUSTOM_THEME_CONTACT ?? '1300 141 161',
  EMAIL: import.meta.env.VITE_APP_CUSTOM_THEME_EMAIL ?? 'hello@bridgit.com.au',
  DISCLAIMER:
    import.meta.env.VITE_APP_CUSTOM_THEME_DISCLAIMER ??
    'Eligibility and approval is subject to standard credit assessment and not all amounts, term lengths or rates will available to all applications. Fees, terms and conditions apply.',
  PRIVACY_POLICY_URL:
    import.meta.env.VITE_APP_CUSTOM_THEME_PRIVACY_POLICY_URL ?? 'https://www.bridgit.com.au/privacy-policy',
  TERMS_AND_CONDITIONS_URL:
    import.meta.env.VITE_APP_CUSTOM_THEME_TERMS_AND_CONDITIONS_URL ?? 'https://www.bridgit.com.au/terms-conditions',
  BROKER_TERMS_AND_CONDITIONS_URL:
    import.meta.env.VITE_APP_CUSTOM_THEME_BROKER_TERMS_AND_CONDITIONS_URL ??
    'https://www.bridgit.com.au/broker-terms-and-conditions',
  BROKER_PORTAL_URL: import.meta.env.VITE_APP_CUSTOM_THEME_BROKER_PORTAL_URL ?? 'https://www.bridgit.com.au',
};

export const defaultMaxSecondaryApplicants = 1;

/**
 * Application context constants for determining where the application originated
 */
export const APPLICATION_CONTEXT = {
  CRM: 'crm',
} as const;
